import type {
  LngLat,
  Shape,
} from "../adapters/types";
import {
  lngLatToMercator,
  mercatorToLngLat,
} from "../loaders/formats/shared";
import {
  mapRings,
  withRings,
} from "./shapeRings";

/**
 * Déplace un groupe de formes pour que le centre de sa boîte englobante tombe sur `at`, en gardant
 * leurs positions relatives et leur taille réelle — utilisé pour coller des formes venues d'une
 * autre carte (voir `TokaMapConfig.clipboard`), placée ailleurs dans le monde.
 *
 * Le déplacement se fait en Mercator, pas en longitude/latitude : un degré de longitude n'a pas la
 * même longueur à toutes les latitudes, translater en degrés déformerait les formes. Et comme
 * l'échelle Mercator elle-même varie avec la latitude, le groupe est remis à l'échelle (rapport des
 * cosinus des deux latitudes) pour garder ses dimensions au sol.
 *
 * Seuls le contour et les trous changent ; le reste de chaque forme est repris tel quel.
 */
export function relocateShapes(shapes: Shape[], at: LngLat): Shape[] {
  const points = shapes.flatMap((shape) => shape.coordinates.map(lngLatToMercator));
  if (!points.length) return shapes;

  const xs = points.map(([x]) => x);
  const ys = points.map(([, y]) => y);
  const from: [number, number] = [(Math.min(...xs) + Math.max(...xs)) / 2, (Math.min(...ys) + Math.max(...ys)) / 2];
  const to = lngLatToMercator(at);
  const latitude = (point: [number, number]) => (mercatorToLngLat(point)[1] * Math.PI) / 180;
  const scale = Math.cos(latitude(from)) / Math.cos(latitude(to));

  const relocate = (ring: LngLat[]) =>
    ring.map((coordinate) => {
      const [x, y] = lngLatToMercator(coordinate);
      return mercatorToLngLat([to[0] + (x - from[0]) * scale, to[1] + (y - from[1]) * scale]);
    });

  return shapes.map((shape) => withRings(shape, mapRings(shape, relocate)));
}
